import React from 'react';
import PropTypes from 'prop-types';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {Box, IconButton, Modal} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {Button} from 'wobi-web-common/dist/v2/index.js';
import {useIntl} from 'react-intl';

import {IconBox} from '../IconBox';

function BaggageCoverageOfferModal({
  open,
  onAdd,
  onDecline,
  price,
  travelerCount,
  dayCount,
  loading,
}) {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={onDecline}
      sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: {xs: 2, sm: 3}}}
    >
      <Box
        role='dialog'
        aria-modal='true'
        aria-labelledby='baggage-coverage-offer-title'
        aria-describedby='baggage-coverage-offer-description'
        dir='rtl'
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 440,
          maxHeight: 'calc(100dvh - 32px)',
          overflowY: 'auto',
          borderRadius: `${theme.shape.borderRadius}px`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[12],
          outline: 0,
        }}
      >
        <Box
          data-testid='baggage-offer-header'
          sx={{
            position: 'relative',
            minHeight: {xs: 148, sm: 164},
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.travel.baggageOfferHeader,
          }}
        >
          <IconButton
            onClick={onDecline}
            disabled={loading}
            aria-label={intl.formatMessage({id: 'baggageOffer.close'})}
            sx={{
              position: 'absolute',
              insetInlineEnd: 14,
              top: 14,
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.background.paper,
              '&:hover': {backgroundColor: theme.palette.action.hover},
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              insetInlineStart: 16,
              top: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              padding: '4px 10px',
              borderRadius: `${theme.shape.borderRadius}px`,
              color: theme.palette.success.dark,
              backgroundColor: theme.palette.success.light,
              fontSize: theme.typography.pxToRem(13),
              fontWeight: theme.typography.fontWeightSemiBold,
              lineHeight: 1.25,
            }}
          >
            <AutoAwesomeOutlinedIcon aria-hidden='true' sx={{fontSize: 15}} />
            {intl.formatMessage({id: 'baggageOffer.recommended'})}
          </Box>

          <IconBox icon='suitcase' size={96} elevated />
        </Box>

        <Box sx={{padding: {xs: '26px 24px 24px', sm: '28px 26px 28px'}, textAlign: 'center'}}>
          <Box
            id='baggage-coverage-offer-title'
            component='h2'
            sx={{
              margin: 0,
              color: theme.palette.text.primary,
              fontSize: {xs: theme.typography.pxToRem(24), sm: theme.typography.pxToRem(26)},
              fontWeight: theme.typography.fontWeightBold,
              lineHeight: 1.2,
            }}
          >
            {intl.formatMessage({id: 'baggageOffer.title'})}
          </Box>
          <Box
            id='baggage-coverage-offer-description'
            component='p'
            sx={{
              maxWidth: 350,
              margin: '10px auto 0',
              color: theme.palette.text.secondary,
              fontSize: theme.typography.pxToRem(16),
              lineHeight: 1.5,
            }}
          >
            {intl.formatMessage({id: 'baggageOffer.description'})}
          </Box>
          <Box
            component='p'
            sx={{
              margin: '12px 0 0',
              color: theme.palette.text.secondary,
              fontSize: theme.typography.pxToRem(13),
              lineHeight: 1.4,
            }}
          >
            {intl.formatMessage({id: 'baggageOffer.costSummary'}, {
              price: Number(price).toFixed(2),
              travelers: travelerCount,
              days: dayCount,
            })}
          </Box>

          <Button
            data-testid='add-baggage-coverage'
            data-autofocus
            variant='accent'
            size='lg'
            block
            loading={loading}
            onClick={onAdd}
            style={{marginTop: 20, borderRadius: theme.shape.borderRadius}}
          >
            {intl.formatMessage({id: 'baggageOffer.add'})}
          </Button>
          <Button
            data-testid='decline-baggage-coverage'
            variant='ghost'
            disabled={loading}
            onClick={onDecline}
            style={{marginTop: 8, textDecoration: 'underline'}}
          >
            {intl.formatMessage({id: 'baggageOffer.decline'})}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

BaggageCoverageOfferModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  travelerCount: PropTypes.number.isRequired,
  dayCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
};

BaggageCoverageOfferModal.defaultProps = {
  loading: false,
};

export {BaggageCoverageOfferModal};